import { gql, makeVar, NetworkStatus, useQuery, useReactiveVar } from '@apollo/client';
import * as React from 'react';

import { PaginationDirection } from '../../__generated__/globalTypes';
import { WorkItemFragment } from '../../components/alerts/workItemFragment';

import type { GetWorkItemsQuery, GetWorkItemsQueryVariables } from './__generated__/useWorkItems.gql';
import type { WorkItemFiltersInput } from '../../__generated__/globalTypes';
import type { WorkItemDetailsFragment } from '../../components/alerts/__generated__/workItemFragment.gql';
import type { ReactiveVar } from '@apollo/client';

const GET_WORK_ITEMS = gql`
  query GetWorkItems($pagination: PaginationInput!, $filters: WorkItemFiltersInput) {
    getWorkItems(pagination: $pagination, filters: $filters) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          ...WorkItemDetails
        }
      }
    }
  }
  ${WorkItemFragment}
`;

const DEFAULT_PAGE_SIZE = 20;

/**
 * Mirrors `MAX_LIMIT` in the resolver. Past it the server clamps and answers the
 * same rows under a wider limit while still reporting `hasNextPage`, so an
 * unclamped window would let the need-attention auto-fetch widen forever against
 * a page that cannot grow.
 */
const MAX_WINDOW_ROWS = 500;

/**
 * The one live (unarchived) read every work-item alert surface shares. Two
 * surfaces passing the same filters share a cache entry and a paging window, so
 * they must also pass {@link WORK_ITEM_ALERTS_PAGE_SIZE} — see
 * [work-item alert reads](../../../docs/work-item-alert-reads.md).
 */
export const UNARCHIVED_WORK_ITEM_FILTERS: WorkItemFiltersInput = { archived: false };

/** The history read's filters, the archived half of {@link UNARCHIVED_WORK_ITEM_FILTERS}. */
export const ARCHIVED_WORK_ITEM_FILTERS: WorkItemFiltersInput = { archived: true };

/** Rows per request for the shared live read — see {@link UNARCHIVED_WORK_ITEM_FILTERS}. */
export const WORK_ITEM_ALERTS_PAGE_SIZE = DEFAULT_PAGE_SIZE;

/**
 * How deep each cache entry is currently read. Depth has to be a *query
 * variable* shared by every reader of one entry, or an eviction's re-run
 * replays page one over the loaded list — see
 * [work-item alert reads](../../../docs/work-item-alert-reads.md).
 */
const windowVarsByKey = new Map<string, ReactiveVar<number>>();

/** Key on the filters' *value*: identity would give two equal filters separate windows over one entry. */
function windowKey(filters: WorkItemFiltersInput | undefined): string {
  if (!filters) {
    return '{}';
  }

  const definedEntries = Object.entries(filters)
    .filter(([, value]) => value !== undefined)
    .sort(([left], [right]) => left.localeCompare(right));

  return JSON.stringify(definedEntries);
}

function getWindowVar(key: string, pageSize: number): ReactiveVar<number> {
  const existing = windowVarsByKey.get(key);
  if (existing) {
    return existing;
  }

  const created = makeVar(pageSize);
  windowVarsByKey.set(key, created);
  return created;
}

/**
 * Drop every window back to its first page. Module state outlives the React
 * tree and the Apollo cache, so a test or a story that paged deep would
 * otherwise change the variables the next one issues.
 */
export function resetWorkItemWindows(): void {
  windowVarsByKey.clear();
}

interface IUseWorkItemsOptions {
  filters?: WorkItemFiltersInput;
  pageSize?: number;
  skip?: boolean;
}

/**
 * One paged read of the household's work-item alerts feed, newest-created
 * first, with its own "show more".
 *
 * Pages by **widening the window** rather than chaining cursors: "show more"
 * raises `pagination.limit`, so the loaded depth is part of the query's
 * variables and survives every refetch, including the one an archive's eviction
 * drives. Every reader of a cache entry shares one window (see
 * {@link windowVarsByKey}), so two mounted surfaces cannot race each other back
 * to page one. `cache-and-network` covers the re-read rather than the widen: a
 * widen lands on a depth of its own and misses the cache either way, but a
 * remount at a depth the cache still holds renders it while it revalidates,
 * which a clock-derived `status` needs.
 *
 * Reads through `previousData` because archiving anywhere evicts the whole
 * `getWorkItems` field — without it the list blinks out from under the click
 * that filed a row. `isLoadingMore` keys on the variables-changed status rather
 * than `loading`, so only a widen the user asked for puts a spinner on "Show
 * more" — an eviction-driven refetch re-runs at unchanged variables.
 *
 * A row's `status` is derived from the request clock, so a cached page goes
 * stale by time alone as due dates cross now. `refetch` is the resync lever: a
 * surface reading polled counts beside this list refetches on a counts
 * transition rather than polling the list itself.
 *
 * @param options.filters - work-item filters; a counts read beside this one
 *   must be passed the same, or the two describe different sets.
 * @param options.pageSize - rows a `loadMore` adds to the window.
 * @param options.skip - skip the query (e.g. while the gate is off).
 * @returns the loaded rows, `hasMore`/`loadMore` for the next page,
 *   `hasReachedEnd` once the server has said the loaded rows are all there
 *   are, and the read's own loading/error state.
 */
export const useWorkItems = ({ filters, pageSize = DEFAULT_PAGE_SIZE, skip }: IUseWorkItemsOptions = {}) => {
  const key = windowKey(filters);
  const windowVar = React.useMemo(() => getWindowVar(key, pageSize), [key, pageSize]);
  const limit = useReactiveVar(windowVar);

  const { data, previousData, loading, error, networkStatus, refetch } = useQuery<
    GetWorkItemsQuery,
    GetWorkItemsQueryVariables
  >(GET_WORK_ITEMS, {
    variables: { pagination: { limit, direction: PaginationDirection.Forward }, filters },
    skip,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  const connection = (data ?? previousData)?.getWorkItems;
  const workItems: WorkItemDetailsFragment[] = React.useMemo(
    () => connection?.edges?.map((edge) => edge.node) ?? [],
    [connection]
  );

  const hasNextPage = connection?.pageInfo?.hasNextPage ?? false;
  const hasMore = hasNextPage && limit < MAX_WINDOW_ROWS;
  // Not `!hasMore`: a window clamped at its ceiling stops paging while the
  // server still holds rows, and a read that has not landed has said nothing.
  const hasReachedEnd = connection !== undefined && !hasNextPage;
  const loadedCount = workItems.length;

  // Widens to a target rather than by a step, so the concurrent callers this
  // has — two mounted surfaces, each running the need-attention auto-fetch —
  // converge on one page instead of stacking several.
  const loadMore = React.useCallback(() => {
    if (!hasMore) {
      return;
    }

    const target = Math.min(loadedCount + pageSize, MAX_WINDOW_ROWS);
    if (target > windowVar()) {
      windowVar(target);
    }
  }, [hasMore, loadedCount, pageSize, windowVar]);

  return {
    workItems,
    hasMore,
    hasReachedEnd,
    isLoadingMore: networkStatus === NetworkStatus.setVariables,
    loading,
    error,
    loadMore,
    refetch,
  };
};
